import { liteClient as algoliasearch } from 'algoliasearch/lite'

// Client-side station search over the tide + current Algolia indices, ported
// from the native app + our legacy explore search. Cost-conscious like native:
// credentials fetched once (memoized + in-flight deduped), a memory response
// cache keyed by query+rounded-geo, min-2-char queries, and aroundLatLng rounded
// to 2 dp so proximity doesn't bust cache keys (proximity is a ranking boost via
// aroundRadius:'all', not a filter).

const MIN_QUERY = 2
const HITS_PER_INDEX = 8
const CACHE_MAX = 40

// Memoized client promise (built once; concurrent callers share it). Resolves to
// { client, indices } or null when search isn't available.
let clientPromise = null

async function buildClient() {
  // Local-dev override: build the client directly from the PUBLIC, read-only
  // Algolia app id + search key (the same values production ships to every
  // browser). Queries the production index on algolia.net — no credentials API
  // call, no server, no CORS. Set these in a git-ignored .env for local testing.
  const envApp = import.meta.env.VITE_ALGOLIA_APP_ID
  const envKey = import.meta.env.VITE_ALGOLIA_SEARCH_KEY
  if (envApp && envKey) {
    const env = import.meta.env.VITE_ALGOLIA_INDEX_ENV || 'production'
    return {
      client: algoliasearch(envApp, envKey),
      indices: {
        tide_stations: `TideStation_${env}`,
        current_stations: `CurrentStation_${env}`,
      },
    }
  }
  // Otherwise fetch credentials from the API. Same-origin by default; set
  // VITE_API_BASE to point at another origin (e.g. https://tidesnear.me — that
  // origin must allow CORS for the browser call to succeed).
  const base = import.meta.env.VITE_API_BASE || ''
  try {
    const r = await fetch(`${base}/api/v2/search/credentials`)
    if (!r.ok) return null
    const creds = await r.json()
    if (!creds || !creds.app_id || !creds.search_key || !creds.indices) return null
    return { client: algoliasearch(creds.app_id, creds.search_key), indices: creds.indices }
  } catch {
    return null
  }
}

function loadClient() {
  if (clientPromise) return clientPromise
  // Reset on failure so a later query retries (transient network / not-yet-ready).
  clientPromise = buildClient().then((c) => {
    if (!c) clientPromise = null
    return c
  })
  return clientPromise
}

// Small LRU-ish results cache (Map preserves insertion order).
const resultsCache = new Map()
function cacheGet(key) {
  if (!resultsCache.has(key)) return undefined
  const val = resultsCache.get(key)
  resultsCache.delete(key)
  resultsCache.set(key, val) // bump to most-recent
  return val
}
function cacheSet(key, val) {
  resultsCache.set(key, val)
  if (resultsCache.size > CACHE_MAX) resultsCache.delete(resultsCache.keys().next().value)
}

function mapHit(hit, kind) {
  return {
    id: hit.objectID ?? hit.id,
    name: hit.name,
    kind,
    city_name: hit.city_name,
    state_name: hit.state_name,
    state_abbr: hit.state_abbr,
    country_name: hit.country_name,
    country_code: hit.country_code,
    lat: hit._geoloc?.lat ?? hit.lat,
    lng: hit._geoloc?.lng ?? hit.lng,
  }
}

// Returns { status: 'ok'|'unavailable', tide: [...], current: [...] }.
export async function searchStations(query, coords) {
  const q = (query || '').trim()
  if (q.length < MIN_QUERY) return { status: 'ok', tide: [], current: [] }

  const around = coords ? `${coords.lat.toFixed(2)},${coords.lng.toFixed(2)}` : ''
  const key = `${q.toLowerCase()}|${around}`
  const cached = cacheGet(key)
  if (cached) return cached

  const algolia = await loadClient()
  if (!algolia) return { status: 'unavailable', tide: [], current: [] }

  const { client, indices } = algolia
  const geo = around ? { aroundLatLng: around, aroundRadius: 'all' } : {}
  let res
  try {
    res = await client.search([
      {
        indexName: indices.tide_stations,
        query: q,
        params: { hitsPerPage: HITS_PER_INDEX, ...geo },
      },
      {
        indexName: indices.current_stations,
        query: q,
        params: { hitsPerPage: HITS_PER_INDEX, ...geo },
      },
    ])
  } catch {
    return { status: 'unavailable', tide: [], current: [] }
  }
  const out = {
    status: 'ok',
    tide: (res.results[0]?.hits || []).map((h) => mapHit(h, 'tide')),
    current: (res.results[1]?.hits || []).map((h) => mapHit(h, 'current')),
  }
  cacheSet(key, out)
  return out
}

// Great-circle distance in meters between two {lat,lng} points.
export function haversineMeters(a, b) {
  if (a?.lat == null || a?.lng == null || b?.lat == null || b?.lng == null) return null
  const R = 6371000
  const toRad = (d) => (d * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)))
}
